/**
 * Phase 5 — offline-capable kiosk writes.
 *
 * Check-in / check-out are the two kiosk actions that must never be lost when
 * a tablet drops off Wi-Fi. Both go through the shared write queue: sent
 * immediately when online, persisted and replayed on reconnect otherwise.
 */
import { supabase } from '@/integrations/supabase/client';
import { registerWriteHandler, writeOrQueue } from './queue';

export const KIOSK_WRITE_KIND = 'kiosk-public';

let registered = false;

export function registerKioskWriteHandler() {
  if (registered) return;
  registered = true;
  registerWriteHandler(KIOSK_WRITE_KIND, async (payload) => {
    const { data, error } = await supabase.functions.invoke('kiosk-public', {
      body: payload as Record<string, unknown>,
    });
    if (error) throw error;
    const res = data as { ok?: boolean; error?: string } | null;
    // A rejected-but-delivered write (e.g. "visit_already_closed") must not be
    // retried forever — only transport failures throw.
    // Transient/auth refusals ("locked", closed session, server hiccup) are
    // retried so the sign-in is kept on the tablet until it is accepted.
    if (res && res.ok === false) {
      const retryable = ['locked', 'session_invalid_or_closed', 'session_required', 'checkin_failed', 'checkout_failed'];
      if (!res.error || retryable.includes(res.error)) throw new Error(res.error ?? 'replay_failed');
      console.warn('[kiosk-queue] replay rejected:', res.error);
    }
  });
}

export async function kioskWriteOrQueue(payload: Record<string, unknown>) {
  registerKioskWriteHandler();
  return writeOrQueue(KIOSK_WRITE_KIND, { queued_at: Date.now(), ...payload });
}
